/**
 * Liquid-glass recipe for bottom sheets: the share sheet and the profile
 * actions sheet.
 *
 * It follows the app's existing glass recipe (ConnectButton, LocationDrawer's
 * `GLASS_RAISED`): a low-alpha tint over a backdrop that is blurred AND
 * saturated. The saturate step is what makes it look like glass rather than a
 * grey wash. The overlay is lighter than the stock drawer overlay, so the page
 * behind can show through the sheet.
 *
 * Only the sheet itself carries `backdrop-filter`. A filtered element is a
 * backdrop root, so a second blur on the rows would only resample the sheet's
 * own fill. The grouped surfaces inside are plain tints with a lit top edge.
 *
 * Every light class has a `dark:` twin. These are passed to `DrawerContent`,
 * whose base carries `dark:bg-black/90`, and an unprefixed class never beats a
 * `dark:` one in the merge (see the ghost/default Button trap).
 */

export const GLASS_SHEET_OVERLAY =
  "bg-black/25 backdrop-blur-[3px] dark:bg-black/50";

export const GLASS_SHEET =
  "rounded-t-[28px] border-white/60 bg-white/72 p-0 backdrop-blur-2xl backdrop-saturate-[1.8] shadow-[0_-12px_40px_rgba(15,23,42,0.18),inset_0_1px_0_rgba(255,255,255,0.85)] dark:border-white/12 dark:bg-[#141416]/72 dark:shadow-[0_-12px_40px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.12)]";

/** An inset grouped list: rows share one rounded glass slab. */
export const GLASS_GROUP =
  "overflow-hidden rounded-[20px] border border-black/[0.05] bg-white/65 shadow-[0_1px_2px_rgba(15,23,42,0.05),inset_0_1px_0_rgba(255,255,255,0.8)] dark:border-white/[0.08] dark:bg-white/[0.06] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]";

/** Hairlines between rows of a `GLASS_GROUP`. */
export const GLASS_GROUP_DIVIDE =
  "divide-y divide-black/[0.06] dark:divide-white/[0.07]";

/** A tappable row inside a `GLASS_GROUP`. */
export const GLASS_ROW =
  "flex min-h-[52px] w-full items-center gap-3 px-3.5 text-left text-[15px] text-text-primary transition-colors duration-150 ease-out-strong active:bg-black/[0.05] disabled:opacity-60 pointer-fine:hover:bg-black/[0.03] dark:text-white dark:active:bg-white/[0.07] dark:pointer-fine:hover:bg-white/[0.04]";

/** A standalone glass button: quick-action tiles, Cancel. */
export const GLASS_TILE =
  "border border-black/[0.05] bg-white/65 text-text-primary shadow-[0_1px_2px_rgba(15,23,42,0.05),inset_0_1px_0_rgba(255,255,255,0.8)] transition-[transform,background-color] duration-150 ease-out-strong active:scale-[0.97] active:bg-white/90 disabled:opacity-60 dark:border-white/[0.08] dark:bg-white/[0.06] dark:text-white dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] dark:active:bg-white/[0.11]";

/** A recessed glass field (search, message). */
export const GLASS_FIELD =
  "border border-black/[0.05] bg-black/[0.04] dark:border-white/[0.08] dark:bg-white/[0.07]";

/** The small squircle that holds a row's icon. */
export const GLASS_ICON =
  "flex size-8 shrink-0 items-center justify-center rounded-[10px] [&_svg]:size-[18px]";

export const GLASS_ICON_TONE = {
  neutral:
    "bg-black/[0.06] text-text-primary dark:bg-white/[0.1] dark:text-white",
  brand: "bg-brand/12 text-brand dark:bg-brand/25 dark:text-violet-300",
  danger: "bg-red-500/12 text-red-600 dark:bg-red-500/20 dark:text-red-400",
} as const;

/** Text for destructive rows. */
export const GLASS_DANGER_TEXT = "text-red-600 dark:text-red-400";

/** Muted secondary text on glass. */
export const GLASS_MUTED_TEXT = "text-text-tertiary dark:text-white/55";

/**
 * The primary action. Written out rather than `<Button className="bg-brand">`,
 * whose default variant's `dark:bg-white/[0.08]` would win in dark mode.
 */
export const GLASS_PRIMARY_BUTTON =
  "flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand font-semibold text-[15px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_8px_20px_-8px_rgba(124,58,237,0.6)] transition-[transform,background-color,opacity] duration-150 ease-out-strong active:scale-[0.98] disabled:opacity-70 pointer-fine:hover:bg-brand-hover dark:bg-brand dark:text-white";

/** The destructive confirm. */
export const GLASS_DANGER_BUTTON =
  "flex h-12 w-full items-center justify-center gap-2 rounded-full bg-red-600 font-semibold text-[15px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_8px_20px_-8px_rgba(220,38,38,0.55)] transition-[transform,background-color,opacity] duration-150 ease-out-strong active:scale-[0.98] disabled:opacity-70 pointer-fine:hover:bg-red-700 dark:bg-red-500 dark:text-white dark:pointer-fine:hover:bg-red-600";
